import { FC } from 'react'

import { Button, ButtonSizes, ButtonVariants } from '@inno-ui/button'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { ArtifactReviewStatus, ArtifactSummary } from 'shared/api/forgeApi'

import styles from './StatusBanner.module.scss'

interface StatusBannerProps {
  artifact: ArtifactSummary
  status: ArtifactReviewStatus | undefined
  isShared: boolean
  busy: boolean
  onResubmit: () => void
}

// where the owner's share request stands: pending / declined / approved
export const StatusBanner: FC<StatusBannerProps> = ({
  artifact,
  status,
  isShared,
  busy,
  onResubmit
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })

  if (status === 'pending') {
    return (
      <div className={cn(styles.statusBanner, styles.statusPending)}>
        <span className={styles.statusTitle}>{t('status_pending')}</span>
        <span className={styles.statusText}>{t('status_pending_hint')}</span>
      </div>
    )
  }

  if (status === 'declined') {
    return (
      <div className={cn(styles.statusBanner, styles.statusDeclined)}>
        <span className={styles.statusTitle}>
          {artifact.reviewed_by_name
            ? t('status_declined_by', { name: artifact.reviewed_by_name })
            : t('status_declined')}
        </span>
        <span className={styles.statusText}>
          {artifact.review_reason
            ? t('status_declined_reason', { reason: artifact.review_reason })
            : t('status_declined_no_reason')}
        </span>
        <span className={styles.statusText}>{t('status_declined_retry')}</span>
        {isShared && (
          <div className={styles.statusAction}>
            <Button
              label={t('resubmit')}
              size={ButtonSizes.S}
              variant={ButtonVariants.Dark}
              disabled={busy}
              onClick={onResubmit}
            />
          </div>
        )}
      </div>
    )
  }

  if (status === 'approved' && isShared) {
    return (
      <div className={cn(styles.statusBanner, styles.statusApproved)}>
        <span className={styles.statusTitle}>{t('status_approved')}</span>
        <span className={styles.statusText}>{t('status_approved_hint')}</span>
      </div>
    )
  }

  return null
}
